import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import './Generator.css';
import api from '../api';
import TaskGrid from './TaskGrid';
import LoadingButton from '../UI/LoadingButton';
import { AuthContext } from '../Login/AuthContext';

const Generator = () => {
    const [description, setDescription] = useState('');
    const [chapters, setChapters] = useState(3);
    const [subChapters, setSubChapters] = useState(2);
    const [language, setLanguage] = useState('english');
    const [type, setType] = useState('guide');
    const [authorName, setAuthorName] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [charCount, setCharCount] = useState(0);
    const [showPricing, setShowPricing] = useState(false);

    const navigate = useNavigate();
    const taskGridRef = useRef();
    const { user } = useContext(AuthContext);

    const handleDescriptionChange = (e) => {
        const text = e.target.value;
        if (text.length <= 2000) {
            setDescription(text);
            setCharCount(text.length);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsGenerating(true);
        setError(null);

        try {
            await api.post('/generate', {
                description,
                chapters,
                sub_chapters: subChapters,
                language,
                type,
                author_name: authorName || 'Author'
            });
            
            // Reset form
            setDescription('');
            setAuthorName('');
            setCharCount(0);
            
            // Force refresh des tasks
            taskGridRef.current.fetchTasks();
            
        } catch (err) {
            setError(err.response?.data?.error || 'An error occurred while generating the book');
        } finally {
            setIsGenerating(false);
        }
    };

    const openPricing = () => {
        navigate('/settings', { state: { openPricing: true } });
    };

    const hasPremiumAccess = (user && ((user.subscription && !user.subscription.payment_failed) || user.byok));

    return (
        <>
            <div className="generator">
                {!hasPremiumAccess ? (
                    <div className="premium-feature-panel">
                        <div className="premium-feature-content">
                            <div className="premium-feature-icon">
                                <span className="material-symbols-outlined">auto_stories</span>
                            </div>
                            <h2>Fast Book Generator - Premium Feature</h2>
                            <p>Generate complete books in seconds with our powerful AI technology. Create professional-quality content with customizable chapters, styles, and languages.</p>
                            <ul className="premium-feature-benefits">
                                <li>
                                    <span className="material-symbols-outlined">check_circle</span>
                                    Generate full books in multiple formats and languages
                                </li>
                                <li>
                                    <span className="material-symbols-outlined">check_circle</span>
                                    Customize chapters, structure, and writing style
                                </li>
                                <li>
                                    <span className="material-symbols-outlined">check_circle</span>
                                    Export to PDF, EPUB, or edit directly in our platform
                                </li>
                            </ul>
                            <button className="premium-upgrade-button" onClick={openPricing}>
                                <span className="material-symbols-outlined">upgrade</span>
                                Upgrade to Premium
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="panel">
                        <div className="generator-panel-col">
                            <h2><span className="material-symbols-outlined">book_4</span> Fast Ebook Generator</h2>

                            <form onSubmit={handleSubmit} className="generator-form">
                                <div className="form-group">
                                    <label>Book Description</label>
                                    <textarea
                                        value={description}
                                        className="form-control"
                                        onChange={handleDescriptionChange}
                                        placeholder="Describe the topic of the book and what it talks about"
                                        required
                                    />
                                    <span className="char-count">{charCount}/2000</span>
                                </div>
                                <div className="form-group">
                                    <label>Author Name</label>
                                    <input
                                        type="text"
                                        value={authorName}
                                        onChange={(e) => setAuthorName(e.target.value)}
                                        placeholder="Enter author name (optional)"
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Minimum chapters count</label>
                                    <select
                                        value={chapters}
                                        onChange={(e) => setChapters(Number(e.target.value))}
                                        className="form-control"
                                    >
                                        <option value={2}>2</option>
                                        <option value={3}>3</option>
                                        <option value={4}>4</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Minimum sub-chapters count</label>
                                    <select
                                        value={subChapters}
                                        onChange={(e) => setSubChapters(Number(e.target.value))}
                                        className="form-control"
                                    >
                                        <option value={2}>2</option>
                                        <option value={3}>3</option>
                                        <option value={4}>4</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Book type</label>
                                    <select
                                        value={type}
                                        onChange={(e) => setType(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="guide">Guide / Instructive</option>
                                        <option value="fiction">Story / Narrative</option>
                                        <option value="essay">Essay</option>
                                        <option value="informative">Informative</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Language</label>
                                    <select
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="afrikaans">🇿🇦 Afrikaans</option>
                                        <option value="arabic">🇸🇦 Arabic</option>
                                        <option value="bengali">🇧🇩 Bengali</option>
                                        <option value="bulgarian">🇧🇬 Bulgarian</option>
                                        <option value="chinese_simplified">🇨🇳 Chinese (Simplified)</option>
                                        <option value="chinese_traditional">🇹🇼 Chinese (Traditional)</option>
                                        <option value="croatian">🇭🇷 Croatian</option>
                                        <option value="czech">🇨🇿 Czech</option>
                                        <option value="danish">🇩🇰 Danish</option>
                                        <option value="dutch">🇳🇱 Dutch</option>
                                        <option value="english">🇬🇧 English</option>
                                        <option value="finnish">🇫🇮 Finnish</option>
                                        <option value="french">🇫🇷 French</option>
                                        <option value="german">🇩🇪 German</option>
                                        <option value="greek">🇬🇷 Greek</option>
                                        <option value="hebrew">🇮🇱 Hebrew</option>
                                        <option value="hindi">🇮🇳 Hindi</option>
                                        <option value="hungarian">🇭🇺 Hungarian</option>
                                        <option value="indonesian">🇮🇩 Indonesian</option>
                                        <option value="italian">🇮🇹 Italian</option>
                                        <option value="japanese">🇯🇵 Japanese</option>
                                        <option value="korean">🇰🇷 Korean</option>
                                        <option value="malay">🇲🇾 Malay</option>
                                        <option value="marathi">🇮🇳 Marathi</option>
                                        <option value="norwegian">🇳🇴 Norwegian</option>
                                        <option value="persian">🇮🇷 Persian</option>
                                        <option value="polish">🇵🇱 Polish</option>
                                        <option value="portuguese">🇵🇹 Portuguese</option>
                                        <option value="portuguese_br">🇧🇷 Brazilian Portuguese</option>
                                        <option value="romanian">🇷🇴 Romanian</option>
                                        <option value="russian">🇷🇺 Russian</option>
                                        <option value="serbian">🇷🇸 Serbian</option>
                                        <option value="slovak">🇸🇰 Slovak</option>
                                        <option value="slovenian">🇸🇮 Slovenian</option>
                                        <option value="spanish">🇪🇸 Spanish</option>
                                        <option value="swahili">🇰🇪 Swahili</option>
                                        <option value="swedish">🇸🇪 Swedish</option>
                                        <option value="tagalog">🇵🇭 Tagalog</option>
                                        <option value="tamil">🇮🇳 Tamil</option>
                                        <option value="telugu">🇮🇳 Telugu</option>
                                        <option value="thai">🇹🇭 Thai</option>
                                        <option value="turkish">🇹🇷 Turkish</option>
                                        <option value="ukrainian">🇺🇦 Ukrainian</option>
                                        <option value="urdu">🇵🇰 Urdu</option>
                                        <option value="vietnamese">🇻🇳 Vietnamese</option>
                                    </select>
                                </div>

                                {error && <div className="error-message">{error}</div>}

                                <LoadingButton
                                    isLoading={isGenerating}
                                    disabled={isGenerating}
                                >
                                    <span className="material-symbols-outlined">auto_stories</span>
                                    Generate Book
                                </LoadingButton>
                            </form>
                        </div>
                        <div className="generator-panel-col">
                            <TaskGrid ref={taskGridRef} />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Generator; 